import type { SyncRecordMap, SyncTable } from '@trips/shared/schema';
import { SYNC_TABLES } from '@trips/shared/schema-consts';
import { useCallback, useRef, useSyncExternalStore } from 'react';

/**
 * The whole dataset lives in memory. Screens read from here synchronously, so every
 * navigation renders in one frame; IndexedDB is the durable copy behind it.
 */

export type Tables = { [K in SyncTable]: Map<string, SyncRecordMap[K]> };

export type SessionStatus =
  | 'unknown'
  | 'signed-in'
  | 'signed-out'
  | 'needs-setup'
  | 'needs-pairing';

export interface SyncStatus {
  state: 'idle' | 'syncing' | 'offline' | 'error';
  lastSyncAt: number | null;
  pending: number;
  error: string | null;
}

export interface AppState {
  ready: boolean;
  tables: Tables;
  session: SessionStatus;
  sync: SyncStatus;
  /** Ticks every minute so time-based views (Now card, countdowns) stay current. */
  minute: number;
}

function emptyTables(): Tables {
  return Object.fromEntries(SYNC_TABLES.map((t) => [t, new Map()])) as Tables;
}

let state: AppState = {
  ready: false,
  tables: emptyTables(),
  session: 'unknown',
  sync: { state: 'idle', lastSyncAt: null, pending: 0, error: null },
  minute: Math.floor(Date.now() / 60_000),
};

const listeners = new Set<() => void>();

export function getState(): AppState {
  return state;
}

export function setState(update: Partial<AppState> | ((s: AppState) => Partial<AppState>)) {
  const patch = typeof update === 'function' ? update(state) : update;
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Replaces or adds records in one table, producing a new Map so selectors notice. */
export function upsertRecords<T extends SyncTable>(table: T, records: SyncRecordMap[T][]) {
  if (records.length === 0) return;
  setState((s) => {
    const next = new Map(s.tables[table]) as Tables[T];
    for (const r of records) (next as Map<string, SyncRecordMap[T]>).set(r.id, r);
    return { tables: { ...s.tables, [table]: next } };
  });
}

export function loadTables(tables: Tables) {
  setState({ tables, ready: true });
}

export function setSync(patch: Partial<SyncStatus>) {
  setState((s) => ({ sync: { ...s.sync, ...patch } }));
}

/** Reads a slice of state; re-renders only when the slice changes by reference. */
export function useStore<T>(selector: (s: AppState) => T): T {
  const sel = useRef(selector);
  sel.current = selector;
  const get = useCallback(() => sel.current(state), []);
  return useSyncExternalStore(subscribe, get, get);
}

/**
 * Memoizes a derived value on reference-equal inputs. Inputs are usually table Maps,
 * which change identity only when that table changes.
 */
export function derive<I extends unknown[], R>(
  inputs: (s: AppState) => readonly [...I],
  compute: (...args: I) => R,
): (s: AppState) => R {
  let lastInputs: readonly [...I] | null = null;
  let lastResult: R;
  return (s) => {
    const next = inputs(s);
    if (
      lastInputs &&
      next.length === lastInputs.length &&
      next.every((v, i) => v === lastInputs![i])
    ) {
      return lastResult;
    }
    lastInputs = next;
    lastResult = compute(...(next as I));
    return lastResult;
  };
}

/** Keyed version of `derive`, for per-trip or per-item views. */
export function deriveBy<K, I extends unknown[], R>(
  inputs: (s: AppState, key: K) => readonly [...I],
  compute: (key: K, ...args: I) => R,
  max = 30,
): (s: AppState, key: K) => R {
  const cache = new Map<K, { inputs: readonly [...I]; result: R }>();
  return (s, key) => {
    const next = inputs(s, key);
    const hit = cache.get(key);
    if (hit && hit.inputs.length === next.length && next.every((v, i) => v === hit.inputs[i]))
      return hit.result;
    const result = compute(key, ...(next as I));
    cache.delete(key);
    cache.set(key, { inputs: next, result });
    if (cache.size > max) cache.delete(cache.keys().next().value as K);
    return result;
  };
}

// Keep minute-based views fresh.
if (typeof window !== 'undefined') {
  const tick = () => {
    const m = Math.floor(Date.now() / 60_000);
    if (m !== state.minute) setState({ minute: m });
  };
  setInterval(tick, 15_000);
  document.addEventListener('visibilitychange', tick);
}
